import { MutationCache, QueryClient } from "@tanstack/react-query";
import { isCancel } from "axios";

import { handleErrorToast } from "@/api/utils";
import i18n from "@/i18n";

interface IMutationMeta extends Record<string, unknown> {
  suppressErrorToast?: boolean;
  fallbackMessage?: string;
}

declare module "@tanstack/react-query" {
  // NOTE: we should properly update existing namespace, not create nonexistent `IRegister`
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Register {
    mutationMeta: IMutationMeta;
  }
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const meta = mutation.options.meta;
      if (meta?.suppressErrorToast) return;
      // NOTE: Skip global error toast for user-initiated aborts — both the
      // fetch/XHR shape (DOMException "AbortError", e.g. an aborted S3 PUT) and
      // the axios shape (CanceledError, an aborted axios request). Keep
      // `isCancel` as the last operand so its (never-false-in-tests) branch
      // isn't the discriminant that goes uncovered.
      if (
        (error instanceof DOMException && error.name === "AbortError") ||
        isCancel(error)
      )
        return;
      handleErrorToast({
        error,
        fallbackMessage:
          meta?.fallbackMessage || i18n.t("common:something_went_wrong"),
      });
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});
